import { useEffect, useRef, useState } from 'react';

/**
 * How fast the shown level climbs toward a louder reading, per frame.
 *
 * Quick, because the start of a word is the moment the orb is answering: a slow
 * climb reads as the screen lagging behind the voice.
 */
const ATTACK = 0.3;

/**
 * How fast it falls back, per frame. Slower than the climb on purpose — speech
 * is full of gaps a few frames long, and matching them makes the orb stutter
 * between every syllable instead of riding the sentence.
 */
const RELEASE = 0.08;

/** Movement smaller than this is not visible, so it is not worth a redraw. */
const STEP = 0.005;

function clamp(level: number): number {
  return Math.min(Math.max(level, 0), 1);
}

/**
 * A meter reading turned into something that can be watched.
 *
 * The volume hooks report the raw loudness of a track about thirty times a
 * second with no smoothing of their own, and speech at that resolution is not a
 * curve — it is a spray of unrelated numbers. Painting them straight onto the
 * orb is what made it flicker rather than breathe.
 *
 * So the reading is a target, not a position: every frame the shown level moves
 * part of the way toward it, fast on the way up and slowly on the way down.
 * That is one filter doing two jobs — the jitter is averaged out, and a pause
 * between two words settles instead of snapping shut.
 *
 * Returns `undefined` while there is nothing to measure, and costs nothing
 * then: the frame loop only runs during a call.
 */
export function useSmoothLevel(level: number | undefined): number | undefined {
  const target = useRef(0);
  const [shown, setShown] = useState(0);
  const measuring = level !== undefined;

  useEffect(() => {
    target.current = level === undefined ? 0 : clamp(level);
  }, [level]);

  useEffect(() => {
    if (!measuring) {
      return;
    }

    let frame = 0;
    let current = 0;

    function step(): void {
      const goal = target.current;
      current += (goal - current) * (goal > current ? ATTACK : RELEASE);
      /*
       * Returning the previous value tells React there is nothing to do, so a
       * settled orb stops re-rendering entirely rather than re-painting the
       * same circle sixty times a second.
       */
      setShown((previous) => (Math.abs(previous - current) < STEP ? previous : current));
      frame = requestAnimationFrame(step);
    }

    frame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frame);
  }, [measuring]);

  return measuring ? shown : undefined;
}
